/**
 * severityOptional completion carve-out (s155, D-155.1 — engine fix B).
 *
 * `severityOptional` is a SPEC-ONLY field prop (`shared/template-spec`, no DB column —
 * the planner lens reads it server-side from `TEMPLATE_SPEC`). The client completion
 * engine (`computeSectionProgress` via the `section-completion` adapter) runs off the
 * API's `generic_fields`, which do NOT carry spec-only props, so it needs a
 * client-reachable source for the flag.
 *
 * Rather than bundle the whole `TEMPLATE_SPEC` into the client for a handful of fields,
 * we keep an explicit `slug::fieldKey` allowlist here — the same shape as the small
 * `isCategoryAField` allowlist the client already resolves completion membership with —
 * and PIN it to the spec with a drift guard (`server/__tests__/severity-optional-fields.test.ts`)
 * that fails if the spec's `severityOptional` set ever diverges from this list. No drift,
 * no client bundle bloat.
 *
 * Engine fix B (D-155.1): a `severityOptional` required field is DROPPED from the section-
 * completion denominator entirely (Cat-A-consistent) — it never blocks the green check and
 * never shows phantom progress on an untouched section. It stays "Important" on the planner
 * lens (lens behaviour unchanged). Only REQUIRED, non-severityOptional fields are measured,
 * so a `severityOptional`-but-not-required field (e.g. `full_legal_name`, `isRequired:false`)
 * is a no-op for completion; it is listed here only so the drift guard stays exact.
 */

const key = (sectionSlug: string, fieldKey: string): string => `${sectionSlug}::${fieldKey}`;

/** Every field carrying `severityOptional: true` in the template spec, keyed
 *  `sectionSlug::fieldKey`. Kept exact by the drift guard.
 *
 *  s155 (D-155.7): the set was EMPTY. The four former members (middle_name, name_at_birth,
 *  last_occupation, date_signed) left the optional class once EXECUTOR-COVERAGE found them
 *  form-consumed — they are now normal starred fields with `declineEligible` (the decline is
 *  the executor answer). full_legal_name is derived (excluded from the lens via `derivedFrom`),
 *  so it never needed the cap. The MECHANISM + the D-155.1 engine behaviour + this drift guard
 *  were intentionally KEPT for the next genuinely-soft field; the empty set was a valid state.
 *
 *  🔴 s176 (D-176.14): the next genuinely-soft field arrived, and the empty set is now a set of one.
 *  The STARRED-CRITERION CENSUS walked every letter/form-driven ref through the real assembly engine
 *  and found `about-you::spouse_dob` STARRED with NO consumer — the only spouse field with no
 *  artifact behind its star. It keeps `isRequired` (a plausible future consumer under province/state
 *  expansion) but leaves the completion denominator, so it cannot block a green check for a fact
 *  nothing reads. This is the mechanism's first real exercise: pair it with the spec flag or
 *  `severity-optional-sources.test.ts` fails — the allowlist MIRRORS the spec, it never originates. */
export const SEVERITY_OPTIONAL_FIELDS: ReadonlySet<string> = new Set<string>([
  // 🔴 s189 (D-188.13): `about-you::spouse_dob` REMOVED. It was here on D-176.14's "zero consumers"
  // finding, and that premise is falsified — the field is DRAWN on the ISP-1300 prep sheet as
  // "Survivor Date of Birth" (`estate-documents-pdf.ts:542`). The census walked form field maps, not
  // generated prep sheets, so "no consumer" was only as wide as the surfaces searched. The field is
  // required and counted again; `place_of_birth` is now the set's sole member.
  // D-176.18 — no form cell consumes the free-text city. The federal forms take province-or-country,
  // supplied by `province_of_birth`, which stays starred; this retains planner value and feeds the
  // 74A readiness meter.
  "about-you::place_of_birth",
]);

/** True iff the field is `severityOptional` in the spec (non-blocking for completion). */
export function isSeverityOptionalField(sectionSlug: string, fieldKey: string): boolean {
  return SEVERITY_OPTIONAL_FIELDS.has(key(sectionSlug, fieldKey));
}
